import { CheckCircle2, KeyRound, LockKeyhole, Mail } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";

export function ResetPasswordPage() {
  const { session } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken) {
      setMessage("Sua sessão de segurança expirou. Atualize a página.");
      return;
    }

    if (!token) {
      setMessage("O link de recuperação é inválido.");
      return;
    }

    if (password !== confirmation) {
      setMessage("A confirmação da nova senha não confere.");
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const result = await apiRequest<{ changed: boolean; message: string }>(
        "/auth/client/password-reset/complete",
        {
          method: "POST",
          csrfToken: session.csrfToken,
          body: {
            token,
            username: email,
            password,
            confirmation,
          },
        },
      );

      setSuccess(result.changed);
      setMessage(result.message);
      setPassword("");
      setConfirmation("");
    } catch (error) {
      setMessage(
        error instanceof ApiRequestError
          ? error.message
          : "Não foi possível redefinir sua senha.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="login-brand">
          <img className="login-brand-logo" src="/brand/noverik-logo.png" alt="Noverik Tecnologia" />
          <span className="login-product">SERVICE DESK</span>
        </div>

        <div className="login-copy">
          <span className="eyebrow">SEGURANÇA DA CONTA</span>
          <h1>Recupere seu acesso.</h1>
          <p>Defina uma nova senha para voltar à Central de Atendimento.</p>
          <div className="login-benefits">
            <div><KeyRound size={18} /><span>Link individual e temporário</span></div>
            <div><LockKeyhole size={18} /><span>Política de senha integrada ao osTicket</span></div>
          </div>
        </div>
      </section>

      <section className="login-form-side">
        <form className="login-card" onSubmit={submit}>
          <div className="login-card-head">
            <span>NOVERIK DESK</span>
            <h2>{success ? "Senha redefinida" : "Criar nova senha"}</h2>
            <p>
              {success
                ? "Seu acesso foi atualizado com segurança."
                : "Confirme seu e-mail e escolha uma nova senha."}
            </p>
          </div>

          {success ? (
            <div className="reset-success">
              <CheckCircle2 size={28} />
              <strong>{message}</strong>
              <Link className="button primary login-submit reset-login-link" to="/login">
                Ir para o login
              </Link>
            </div>
          ) : (
            <>
              <label className="field">
                <span>E-mail</span>
                <div className="input-wrap">
                  <Mail size={17} />
                  <input
                    autoFocus
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="voce@empresa.com.br"
                    required
                  />
                </div>
              </label>

              <label className="field">
                <span>Nova senha</span>
                <div className="input-wrap">
                  <LockKeyhole size={17} />
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </div>
              </label>

              <label className="field">
                <span>Confirmar nova senha</span>
                <div className="input-wrap">
                  <LockKeyhole size={17} />
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    required
                  />
                </div>
              </label>

              {message && <div className="login-message">{message}</div>}

              <button className="button primary login-submit" disabled={submitting || !token}>
                {submitting ? "Salvando..." : "Redefinir senha"}
              </button>

              <div className="login-switch">
                <Link to="/login">Voltar para o login</Link>
              </div>
            </>
          )}
        </form>
      </section>
    </main>
  );
}
